import type { Atom } from '@/app/editor/iframe/atom/Atom';
import type { AtomModel, TabSettingsData } from '@/app/editor/iframe/atom/Atom.model';
import { AutoRenderSetting } from '@/app/editor/sidebar/settings/AutoRenderSetting';
import type { AtomRenderHook, AtomRenderInterface } from '@/app/editor/iframe/atom/atom-interface/AtomRenderInterface';
import HELPER from '@/app/utils/HELPER';
import atom from '@/app/editor/iframe/atom';
import replaceSettingsToString from '@/app/utils/replaceSettingsToString';
import { DataManager } from '@/app/editor/iframe/atom/service/DataManager';
import utils from '@/app/editor/iframe/atom/utils/index';
import { base } from '@/app/editor/sidebar/constant';

declare let Liquid: any;

/**
 * Common Atom Render for all Atom
 */
export class BaseAtomRender implements AtomRenderInterface {
  public atom: Atom;

  /**
   * BaseAtomRender
   * @param atom atom
   */
  constructor(atom: Atom) {
    this.atom = atom;
  }

  /**
   * render
   */
  async render() {
    this.renderStyle();
    await this.renderTemplate();
    this.initChilds();
    this.renderScript();
    this.applyEvents();
    // Tạo placeholder cho việc kéo thả
    HELPER.generatePlaceholderForm(this.atom.$el);
    if (this.getAtomModel().isAutorenderSetting) {
      const ar = new AutoRenderSetting(this.atom.$el, this.getAtomModel());
      const settingUpdate: any = ar.changeSetting();
      this.atom.setSettings(settingUpdate);
    }
    const dataSetting: TabSettingsData = {
      $atom: this.atom.$el,
      atom: this.atom.data.atom,
      tab: 'Setting',
      drag: false,
      capture: false,
      forceOpen: false,
    };
    DataManager.registerObserver(this.atom);
    DataManager.setState('updateSettingData', { changed: true, data: dataSetting }, this.atom.getId());
    DataManager.getInstance().notify();
    utils.Init(this.atom.$el, this.atom.data);
  }

  /**
   * renderStyle
   * @param style style
   */
  renderStyle(style?: string) {
    if (!style) {
      style = this.getAtomModel().style;
    }
    style = this.renderSettings(style);
    const $styleContainer = this.atom.get$StyleContainer();
    let $style = $styleContainer.find(`#${base._style}-${this.atom.getId()}`);
    if (!$style.length) {
      $style = jQuery(`<div id="${base._style}-${this.atom.getId()}"></div>`).appendTo($styleContainer);
    }
    $style.html(`<style rel="stylesheet" class="style" type="text/css">${style as string}</style>`);
    return false;
  }

  /**
   * [Save data to Datasource]
   * Gắn script vào divContentBk
   * @param script : Chuỗi script cần chạy
   */
  renderScript(script?: string) {
    if (!script) {
      script = this.getAtomModel().script;
    }
    script = this.renderSettings(script);
    const $scriptContainer = this.atom.get$ScriptContainer();
    let $script = $scriptContainer.find(`#${base._script}-${this.atom.getId()}`);
    if (!$script.length) {
      $script = jQuery(`<div id="${base._script}-${this.atom.getId()}"></div>`).appendTo($scriptContainer);
    }
    $script.find('.script').remove();
    try {
      $script.html(`
        <script class="script" type="text/javascript">
          ${script}
        </script>
      `);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err.message);
    }
    return false;
  }

  /**
   * [Init]
   * Render content bên trong
   */
  initChilds(): void {
    const moduleName = this.atom.getModuleName();
    if (moduleName == 'Tabs') {
      this.atom.$el.find('> .module > ul > li.gf_tab > .item-content').each(function () {
        const $itemContent = jQuery(this);
        $itemContent.children('.element-wrap[data-label="Heading"]').attr('data-tool', '0');
        $itemContent.children('.element-wrap[data-label="Text Block"]').attr('data-tool', '0');
      });
    }
    const _this = this;
    this.get$el()
      .find('.item-content')
      .each(function () {
        const $itemContent = jQuery(this);
        const initCondition = $itemContent.closest('.module-wrap').is(_this.get$el());
        if (initCondition) {
          const $childs = $itemContent.children('.gf_row, .module-wrap, .element-wrap');
          if ($childs && $childs.length) {
            for (let i = 0; i < $childs.length; i++) {
              const $child = jQuery($childs[i]);
              atom.Init($child);
            }
          }
        }
      });
    return;
  }

  /**
   * [Hook]
   * renderTemplate
   */
  async renderTemplate(): Promise<void> {
    let templateTmp = this.getAtomModel().html;
    templateTmp = this.renderSettings(templateTmp); // Render khung cho module và data cho module sử dụng
    let html = await this.renderLiquid(templateTmp); // render child and render liquid
    html = this.renderSettings(html); // Render render setting id module cho các html childs
    this.get$el().html(html);
  }

  /**
   * [Init]
   * Format setting và append vào divContentbk
   * @param str chuỗi setting
   * @returns setting dạng string đã được format
   */
  renderSettings(str: string): string {
    const id = this.atom.getId();
    const idSlug = id.toString().replace(/-/g, '');
    if (str) {
      str = str.replace(/{{(\S*\s+)?module-id(\S*\s+)?}}/g, id);
      str = str.replace(/{{(\S*\s+)?module-id-slug(\S*\s+)?}}/g, idSlug);
    }
    if (!this.getAtomModel().settings) {
      return str;
    }
    str = replaceSettingsToString(this.getAtomModel().settings, str);
    return str;
  }

  /**
   * [Filter] [Init]
   * Parse chuỗi có cấu trúc Liquid thành HTML dạng Promise
   * @param templateTmp chuỗi template có chứa code liquid cần format
   * @returns trả về một chuỗi HTML
   */
  async renderLiquid(templateTmp: string): Promise<string> {
    const template = HELPER.simpleHTMLEntities(templateTmp);
    // Render liquid
    const tmpl = Liquid.parse(template);
    const html: string = tmpl.render({
      ...this.getAtomModel().data,
    });
    return html;
  }

  /**
   * applyEvents
   */
  applyEvents(): void {
    const atomName = this.atom.getLabel();
    const _this = this.atom;
    switch (atomName) {
      case 'Checkbox': {
        this.get$el()
          .find('.elm .gf_checkbox')
          .parent()
          .off('click')
          .on('click', function (e) {
            HELPER.showNotify('You can select the checkbox in live page only.', 'success');

            e.preventDefault();
            e.stopPropagation();
            return false;
          });
        break;
      }
    } // Fix button click new tabs;
    this.get$el()
      .find('a')
      .off('click')
      .on('click.redir', function (e: Event) {
        e.preventDefault();
        const $this = jQuery(this);
        const link = jQuery(this).attr('href');

        if (link && link != '') {
          // Show popup in editor
          if (link.indexOf('#show-') !== -1) {
            const id = link.split('#show-')[1];
            const $target = _this.$gryffBody.find('#' + id);
            if ($target.length > 0) {
              // Show popup
              if ($target.attr('data-label') == 'Popup' && $target.data('gfmodule') != undefined) {
                const moduleId = $target.data('gfmodule').getModuleId();
                const moduleIdSlug = moduleId.toString().replace(/-/g, '');
                const func = 'getPopup' + moduleIdSlug;
                const gfFrame: any = jQuery('#gfFrame')[0];
                if (gfFrame && gfFrame.contentWindow[func]) {
                  gfFrame.contentWindow[func]().show();
                }
              }
            }
          }

          // Hide in Popup
          if (link.indexOf('#hide') !== -1) {
            const $target = $this.closest('[data-label="Popup"]');
            if ($target.length > 0) {
              // hide popup
              if ($target.attr('data-label') == 'Popup' && $target.data('gfmodule') != undefined) {
                const moduleId = $target.data('gfmodule').getModuleId();
                const moduleIdSlug = moduleId.toString().replace(/-/g, '');
                const func = 'getPopup' + moduleIdSlug;
                const gfFrame: any = jQuery('#gfFrame')[0];
                if (gfFrame && gfFrame.contentWindow[func]) {
                  gfFrame.contentWindow[func]().hide();
                }
              }
            }
          }
        }
        return false;
      });
  }

  /**
   * getAtomModel
   * @returns AtomModel
   */
  getAtomModel(): AtomModel {
    return this.atom.data.atom;
  }

  /**
   * get$divContentBk
   * @returns $divContentBk
   */
  get$divContentBk(): JQuery {
    return this.atom.$divContentBk;
  }
  /**
   * get$el
   * @returns $el
   */
  get$el(): JQuery {
    return this.atom.$el;
  }
}

/**
 * BaseAtomRender for "Atom implements AtomRenderHook"
 */
export class HookAtomRender extends BaseAtomRender {
  /**
   * @override render
   */
  async render() {
    try {
      const hook: AtomRenderHook = this.atom as AtomRenderHook;
      if (hook.beforeRender) hook.beforeRender();
      await super.render();
      if (hook.afterRender) {
        hook.afterRender();
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
    }
  }
}
